import * as Sentry from '@sentry/react';

/**
 * A stale client (old deploy still open in a tab) requests a JS chunk whose content-hashed
 * filename no longer exists. Firebase Hosting's SPA rewrite used to mask this as a 200 HTML
 * response, which the dynamic `import()` then failed to parse as JS. See AUDIT-ADMIN-1KV.
 */
const CHUNK_LOAD_ERROR_PATTERN =
	/Unexpected token '<'|Loading chunk|ChunkLoadError|Failed to fetch dynamically imported module/i;

const RELOAD_FLAG_KEY = 'chunk-reload-attempted';

export const isChunkLoadError = (message: string | null | undefined): boolean =>
	!!message && CHUNK_LOAD_ERROR_PATTERN.test(message);

/**
 * Reports the failure (unless already captured elsewhere, e.g. Sentry's ErrorBoundary) and
 * reloads once per session to pick up the current deploy. Guarded by sessionStorage so a
 * genuinely broken deploy (or an offline user) can't trigger a reload loop.
 *
 * Reporting happens before the reload, and we flush Sentry before calling
 * window.location.reload() — otherwise the reload can abort the in-flight captureException
 * request before it reaches Sentry.
 */
export const recoverFromChunkLoadError = async (error: unknown, skipReport = false): Promise<void> => {
	if (sessionStorage.getItem(RELOAD_FLAG_KEY)) return;
	sessionStorage.setItem(RELOAD_FLAG_KEY, '1');

	if (!skipReport) {
		Sentry.captureException(error, { tags: { chunkLoadRecovery: true } });
	}

	await Sentry.flush(2000).catch(() => undefined);
	window.location.reload();
};
